<script setup lang="ts">
import { computed, onMounted } from 'vue'

import BranchPicker from '@/components/location/BranchPicker.vue'
import DeliveryPicker from '@/components/location/DeliveryPicker.vue'
import { ORDER_TYPE, type OrderType } from '@/composables/useDelivery'
import type { LngLat } from '@/composables/useLocation'
import type { LocationChoice } from '@/composables/useLocationConfirm'
import { useLocationStore } from '@/stores/location'
import { FLOW, track } from '@/utils/track'

/**
 * Выбор точки получения — общий для онбординга и диалога с главной.
 * Тип получения приходит снаружи: его выбирают до открытия карты
 * (экран «Тип заказа» или сегмент в шапке), внутри он не меняется.
 *
 * Наружу отдаёт готовый выбор, писать его в состояние — дело страницы:
 * запись нужно откатить, если меню не приняло точку.
 */
interface Props {
  orderType: OrderType
  /** Идёт проверка выбора: перезапрашивается меню */
  saving?: boolean
  /** Куда смотреть при открытии; по умолчанию последняя точка или центр региона */
  center?: LngLat | undefined
  /** Экран открыт диалогом: доставке нужна шапка с возвратом */
  closable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  saving: false,
  center: undefined,
  closable: false,
})

const emit = defineEmits<{
  confirm: [value: LocationChoice]
  back: []
}>()

const locationStore = useLocationStore()

const isDelivery = computed(() => props.orderType === ORDER_TYPE.delivery)

/* Один вход для обоих способов: дальше воронка расходится по `type` */
onMounted(() => track(FLOW.mapOpened, { type: isDelivery.value ? 'delivery' : 'pickup' }))

const center = computed<LngLat>(() => props.center ?? locationStore.mapCenter)

function confirmDelivery(value: { coords: LngLat; address: string; house: string }): void {
  emit('confirm', {
    delivery: ORDER_TYPE.delivery,
    coords: value.coords,
    address: value.address,
    house: value.house,
  })
}

/** У самовывоза координат нет — их обнуляет `useLocationConfirm`. */
function confirmPickup(value: { restaurantId: string }): void {
  emit('confirm', { delivery: ORDER_TYPE.pickup, restaurantId: value.restaurantId })
}
</script>

<template>
  <DeliveryPicker
    v-if="isDelivery"
    :saving="saving"
    :center="center"
    :closable="closable"
    @confirm="confirmDelivery"
    @back="emit('back')"
  />
  <BranchPicker
    v-else
    :branches="locationStore.pickupRestaurants"
    :markers="locationStore.pickupMarkers"
    :saving="saving"
    :center="center"
    @confirm="confirmPickup"
    @back="emit('back')"
  />
</template>
